import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useAppStore } from "../../store";
import type { WikipartyState } from "./types";
import type { LobbyState } from "../../store";
import type { Article } from "../wikidaily/types";
import {
  queueArticleRender,
  type ArticleRenderData,
} from "../../articleRenderQueue";
import { getRequestedGameLanguage } from "../../wikipediaApi";

type WikipartyLocalState = {
  error: string | null;
  // articleHtml only passes the html to the wikipage component when rendering from cach the articleHtml is not touched
  renderedArticleId: number | null;
  searchResults: Article[] | null;
};

type WikipartyStore = {
  gameState: WikipartyState | null;
  lobbyState: LobbyState | null;
  localState: WikipartyLocalState;

  // Computed
  isHost: () => boolean;

  update_game: (state: Partial<WikipartyState>) => void;
  update_lobby: (lobbyState: LobbyState) => void;
  update_search: (searchResults: Article[]) => void;
  removeArticleFromSearch: (articleId: number) => void;
  render_article: (payload: {
    html: string;
    article_id: number;
    title: string;
  }) => void;

  setArticle: (
    article: Article,
    kind: "Start" | "Target",
  ) => { action: string; payload?: unknown };
  removeArticle: (
    id: number,
    index: number | null,
  ) => { action: string; payload?: unknown };
  setTime: (min: number) => { action: string; payload?: unknown };
  startGame: () => { action: string; payload?: unknown };
  endGame: () => { action: string; payload?: unknown };
  suggestArticle: (article: Article) => { action: string; payload?: unknown };
  removeSuggestion: (
    articleId: number,
    suggesterId: string,
  ) => { action: string; payload?: unknown };
  toggleAllowCountries: (allow: boolean) => {
    action: string;
    payload?: unknown;
  };
  toggleAllowSearch: (allow: boolean) => { action: string; payload?: unknown };
  setHiddenSections: (titles: string[]) => {
    action: string;
    payload?: unknown;
  };
  togglePlayUntilAllFinished: (allow: boolean) => {
    action: string;
    payload?: unknown;
  };
};

export const useWikipartyStore = create<WikipartyStore>()(
  immer((set, get) => ({
    gameState: null,
    lobbyState: null,
    localState: {
      error: null,
      renderedArticleId: null,
      searchResults: null,
    },

    isHost: () => {
      const currentPlayerId = useAppStore.getState().id;
      const hostId = get().lobbyState?.host_id;
      return currentPlayerId === hostId;
    },

    update_search: (searchResults) => {
      set((draft) => {
        draft.localState.searchResults = searchResults;
      });
    },

    removeArticleFromSearch: (articleId) => {
      set((draft) => {
        draft.localState.searchResults =
          draft.localState.searchResults?.filter(
            (article) => article.id !== articleId,
          ) ?? null;
      });
    },

    update_game: (state) => {
      const prevPhase = get().gameState?.phase;
      const prevArticleId = get().gameState?.start?.id;

      console.log("[update_game]", {
        phase: state.phase,
        articleId: state.start?.id,
        isHost: get().isHost(),
        prevPhase,
        prevArticleId,
      });

      set((draft) => {
        const language =
          state.language ||
          draft.gameState?.language ||
          getRequestedGameLanguage(useAppStore.getState().lang);

        draft.gameState ??= {} as WikipartyState;
        Object.assign(draft.gameState, state, { language });

        draft.localState.error = null;
      });
    },

    update_lobby: (lobbyState) => {
      console.log("[update_lobby]", {
        players: Object.keys(lobbyState.players),
        host_id: lobbyState.host_id,
      });

      set((draft) => {
        draft.lobbyState = lobbyState;
        // Also initialize players in gameState if they don't exist yet
        if (draft.gameState && !draft.gameState.players) {
          draft.gameState.players = {};
        }
        draft.localState.error = null;
      });
    },

    render_article: (payload) => {
      const currentPlayerId = useAppStore.getState().id;

      console.log("[render_article]", {
        htmlLength: payload.html?.length,
        id: payload.article_id,
        currentPlayerId,
      });

      const data: ArticleRenderData = {
        html: payload.html,
        articleId: payload.article_id,
        articleTitle: payload.title,
      };
      queueArticleRender(data);
    },

    setArticle: (article, kind) => {
      return {
        action: "set_article",
        payload: { article, kind },
      };
    },

    removeArticle: (id, index) => {
      return {
        action: "remove_article",
        payload: { id, index },
      };
    },

    setTime: (min) => {
      return {
        action: "set_time",
        payload: { min },
      };
    },

    startGame: () => {
      return {
        action: "start",
      };
    },

    endGame: () => {
      return {
        action: "end",
      };
    },

    suggestArticle: (article) => {
      return {
        action: "suggest_article",
        payload: { article },
      };
    },

    removeSuggestion: (articleId, suggesterId) => {
      return {
        action: "remove_suggestion",
        payload: { article_id: articleId, suggester_id: suggesterId },
      };
    },

    toggleAllowCountries: (allow) => {
      return {
        action: "change_setting",
        payload: {
          change_settings: { AllowCountries: allow },
        },
      };
    },

    toggleAllowSearch: (allow) => {
      return {
        action: "change_setting",
        payload: {
          change_settings: { AllowSearch: allow },
        },
      };
    },

    setHiddenSections: (titles) => {
      return {
        action: "change_setting",
        payload: {
          change_settings: { HiddenSections: titles },
          search_query: null,
        },
      };
    },

    togglePlayUntilAllFinished: (allow) => {
      return {
        action: "change_setting",
        payload: {
          change_settings: { PlayUntilAllFinished: allow },
          search_query: null,
        },
      };
    },
  })),
);
